/**
 *
 * Module:        Component
 * Name:          Guru Chat Workspace
 * Description:   Composes desktop sidebar and compact mobile Guru chat history UI
 * Developers:    Adrian Engelmann, Kiran Jayaramu, Nicu Bratescu, Daniela Popescu
 * Copyright:     Copyright 2026 by Qalgo GmbH (www.qalgo.de). All rights reserved.
 *
 */

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
	Box,
	Drawer,
	Snackbar,
	Alert,
	ThemeProvider,
	createTheme,
	useTheme,
	useMediaQuery
} from '@mui/material';
import {resolveChatUiLabels} from '../chatUiReact/qeedist/cjs/mui/chatUiLabels';
import GuruChatSidebar from './GuruChatSidebar';
import GuruChatSearchDialog from './GuruChatSearchDialog';
import GuruFilesSidebar from './GuruFilesSidebar';
import GuruResizableSidebar from './GuruResizableSidebar';
import {GuruDeleteDialog, GuruRenameDialog} from './GuruChatDialogs';
import {
	GURU_SIDEBAR_LIMITS,
	loadGuruLayoutPreferences,
	saveGuruLayoutPreferences
} from './guruLayoutPreferences';
import {createGuruTypography, GURU_FONT_FAMILY} from './guruTypography';
import {
	deleteGuruChat,
	listGuruChats,
	updateGuruChat
} from '../../api/guruChatHistory';

export default function GuruChatWorkspace({
	currentChatId,
	language,
	configuredLabels,
	historyRevision,
	initialHistoryOpen=false,
	interactionDisabled=false,
	onNewChat,
	onSelectChat,
	onLoadGuruPdfs,
	onLoadGuruExcels,
	onDownloadGuruPdf,
	onDownloadGuruExcel,
	renderChat
}) {
	const applicationTheme = useTheme();
	const guruTheme = useMemo(() => createTheme(applicationTheme, {
		typography: createGuruTypography(applicationTheme.typography)
	}), [applicationTheme]);
	const labels = resolveChatUiLabels(language, configuredLabels);
	const desktop = useMediaQuery('(min-width:900px)');
	const [drawerOpen, setDrawerOpen] = useState(Boolean(initialHistoryOpen));
	const [chats, setChats] = useState([]);
	const [loading, setLoading] = useState(true);
	const [actionBusy, setActionBusy] = useState(false);
	const [error, setError] = useState('');
	const [notice, setNotice] = useState('');
	const [renameChat, setRenameChat] = useState(null);
	const [deleteChat, setDeleteChat] = useState(null);
	const [searchOpen, setSearchOpen] = useState(false);
	const [sidebarWidths, setSidebarWidths] = useState(loadGuruLayoutPreferences);
	const {
		leftWidth: leftSidebarWidth,
		rightWidth: rightSidebarWidth,
		recentExpanded,
		filesExpanded
	} = sidebarWidths;
	const resizeLeftSidebar = useCallback(leftWidth => {
		setSidebarWidths(current => ({...current, leftWidth}));
	}, []);
	const resizeRightSidebar = useCallback(rightWidth => {
		setSidebarWidths(current => rightWidth <= GURU_SIDEBAR_LIMITS.right.minWidth
			? {...current, filesExpanded: false}
			: {...current, rightWidth});
	}, []);
	const setRecentExpanded = useCallback(expanded => {
		setSidebarWidths(current => ({...current, recentExpanded: expanded}));
	}, []);
	const setFilesExpanded = useCallback(expanded => {
		setSidebarWidths(current => ({...current, filesExpanded: expanded}));
	}, []);

	const refresh = useCallback(async () => {
		setLoading(true);
		setError('');
		try {
			setChats(await listGuruChats());
		} catch (loadError) {
			console.error('Loading Guru chat history failed:', loadError);
			setError(loadError.message || String(loadError));
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => { refresh(); }, [refresh, historyRevision]);
	useEffect(() => { if (desktop) setDrawerOpen(false); }, [desktop]);
	useEffect(() => { saveGuruLayoutPreferences(sidebarWidths); }, [sidebarWidths]);
	useEffect(() => {
		const handleSearchShortcut = event => {
			if ((event.ctrlKey || event.metaKey) && String(event.key).toLowerCase() === 'k') {
				event.preventDefault();
				setSearchOpen(true);
			}
		};
		window.addEventListener('keydown', handleSearchShortcut);
		return () => window.removeEventListener('keydown', handleSearchShortcut);
	}, []);

	const run = async action => {
		if (actionBusy || interactionDisabled) return false;
		setActionBusy(true);
		try {
			await action();
			await refresh();
			return true;
		} catch (actionError) {
			console.error('Guru chat history action failed:', actionError);
			setNotice(labels.historyActionError);
			return false;
		} finally {
			setActionBusy(false);
		}
	};

	const busy = actionBusy || interactionDisabled;
	const activeChat = chats.find(chat => chat.id === currentChatId) || null;
	const actions = {
		currentChatId,
		labels,
		busy,
		onNewChat: () => run(async () => {
			await onNewChat();
			setDrawerOpen(false);
		}),
		onSelect: chat => run(async () => {
			if (chat.id !== currentChatId) await onSelectChat(chat);
			setDrawerOpen(false);
		}),
		onRename: setRenameChat,
		onTogglePin: chat => run(() => updateGuruChat(chat.id, {pinned: !chat.pinned})),
		onDelete: setDeleteChat
	};

	const sidebar = (
		<GuruChatSidebar
			chats={chats}
			loading={loading}
			error={error}
			onOpenSearch={() => setSearchOpen(true)}
			recentExpanded={recentExpanded}
			onRecentExpandedChange={setRecentExpanded}
			{...actions}
		/>
	);

	return (
		<ThemeProvider theme={guruTheme}>
		<Box sx={{
			height: '100%',
			minHeight: 0,
			display: 'flex',
			overflow: 'hidden',
			fontFamily: GURU_FONT_FAMILY,
			'& input, & textarea, & button': {fontFamily: 'inherit'}
		}}>
			{desktop && (
				<GuruResizableSidebar
					side="left"
					width={leftSidebarWidth}
					minWidth={GURU_SIDEBAR_LIMITS.left.minWidth}
					maxWidth={GURU_SIDEBAR_LIMITS.left.maxWidth}
					label={labels.chatHistory}
					onResize={resizeLeftSidebar}
				>
					{sidebar}
				</GuruResizableSidebar>
			)}
			<Box sx={{flex: '1 1 auto', minWidth: 360, minHeight: 0, display: 'flex', justifyContent: 'center', bgcolor: 'background.default'}}>
				<Box sx={{width: '100%', maxWidth: 980, minWidth: 0, minHeight: 0}}>
					{renderChat({
						openHistory: () => setDrawerOpen(true),
						showHistoryAction: !desktop,
						activeChat,
						chatActionsDisabled: busy,
						onRenameChat: actions.onRename,
						onTogglePinChat: actions.onTogglePin,
						onDeleteChat: actions.onDelete
					})}
				</Box>
			</Box>
			{desktop && (
				<Box
					component="aside"
					sx={{
						flex: `0 0 ${filesExpanded ? rightSidebarWidth : 52}px`,
						width: filesExpanded ? rightSidebarWidth : 52,
						minWidth: filesExpanded ? rightSidebarWidth : 52,
						maxWidth: filesExpanded ? rightSidebarWidth : 52,
						minHeight: 0,
						overflow: 'hidden',
						borderLeft: '1px solid',
						borderColor: 'divider',
						transition: 'width 180ms ease, min-width 180ms ease, max-width 180ms ease, flex-basis 180ms ease',
						'@media (prefers-reduced-motion: reduce)': {transition: 'none'}
					}}
				>
					{filesExpanded ? (
						<GuruResizableSidebar
							component="div"
							showBorder={false}
							side="right"
							width={rightSidebarWidth}
							minWidth={GURU_SIDEBAR_LIMITS.right.minWidth}
							maxWidth={GURU_SIDEBAR_LIMITS.right.maxWidth}
							label={labels.savedFiles}
							onResize={resizeRightSidebar}
						>
							<GuruFilesSidebar
								labels={labels}
								expanded
								onExpandedChange={setFilesExpanded}
								onLoadPdfs={onLoadGuruPdfs}
								onLoadExcels={onLoadGuruExcels}
								onDownloadPdf={onDownloadGuruPdf}
								onDownloadExcel={onDownloadGuruExcel}
							/>
						</GuruResizableSidebar>
					) : (
						<GuruFilesSidebar
							labels={labels}
							expanded={false}
							onExpandedChange={setFilesExpanded}
							onLoadPdfs={onLoadGuruPdfs}
							onLoadExcels={onLoadGuruExcels}
							onDownloadPdf={onDownloadGuruPdf}
							onDownloadExcel={onDownloadGuruExcel}
						/>
					)}
				</Box>
			)}
			{!desktop && (
				<Drawer
					anchor="left"
					open={drawerOpen}
					onClose={() => setDrawerOpen(false)}
					PaperProps={{sx: {width: 'min(92vw, 370px)', maxWidth: '100%'}}}
				>
					{sidebar}
				</Drawer>
			)}
			<GuruRenameDialog
				chat={renameChat}
				labels={labels}
				busy={busy}
				onClose={() => setRenameChat(null)}
				onSave={title => run(async () => {
					await updateGuruChat(renameChat.id, {title});
					setRenameChat(null);
				})}
			/>
			<GuruChatSearchDialog
				open={searchOpen}
				chats={chats}
				labels={labels}
				currentChatId={currentChatId}
				busy={busy}
				onClose={() => setSearchOpen(false)}
				onSelect={actions.onSelect}
				onRename={actions.onRename}
				onTogglePin={actions.onTogglePin}
				onDelete={actions.onDelete}
			/>
			<GuruDeleteDialog
				chat={deleteChat}
				labels={labels}
				busy={busy}
				onClose={() => setDeleteChat(null)}
				onConfirm={() => run(async () => {
					const deletedId = deleteChat.id;
					await deleteGuruChat(deletedId);
					setDeleteChat(null);
					if (deletedId === currentChatId) await onNewChat();
				})}
			/>
			<Snackbar open={Boolean(notice)} autoHideDuration={5000} onClose={() => setNotice('')}>
				<Alert severity="error" onClose={() => setNotice('')}>{notice}</Alert>
			</Snackbar>
		</Box>
		</ThemeProvider>
	);
}
